import { z } from "zod";

/**
 * Frontend mirror of the backend EventType field-schema grammar (NAU-146).
 *
 * The generated `fieldSchema` / `data` types come through orval as
 * `{ [key: string]: unknown }` (the backend types them as freeform objects),
 * so the renderer owns a structured view of the grammar here. Keep this in
 * sync with `event-field-schema.value-object.ts` in core-api.
 */

export type EventFieldType =
  // Launch scalar types — rendered + validated today.
  | "text"
  | "long_text"
  | "numeric"
  | "date"
  | "time"
  | "yes_no"
  | "dropdown"
  // Reserved, forward-compatible types. The grammar accepts them; the renderer
  // skips them until the tickets that introduce them land (NAU-158/159/223).
  | "staff_ref"
  | "file"
  | "group";

export interface EventField {
  key: string;
  label: string;
  type: EventFieldType;
  required?: boolean;
  /** Allowed values when `type === "dropdown"`. */
  options?: string[];
  /** Child fields when `type === "group"` (forward-compat). */
  fields?: EventField[];
  repeating?: boolean;
}

export interface EventFieldSchema {
  fields: EventField[];
}

/**
 * Event type slugs exposed in the UI today. The backend seeds the full
 * framework catalog (fire-drill, incident-report, parent-complaint) so the
 * NAU-146 framework + its tests have data, but only Fire Drill is launched —
 * incident reports and parent complaints are their own tickets (NAU-27/28).
 * Mirrors the log-slug whitelist on LogsPage. Drop slugs in here as those
 * tickets ship.
 */
export const LAUNCHED_EVENT_TYPE_SLUGS: ReadonlyArray<string> = ["fire-drill"];

/** Whether an event type should be shown in the UI (see LAUNCHED_EVENT_TYPE_SLUGS). */
export function isLaunchedEventType(slug: string | null | undefined): boolean {
  return !!slug && LAUNCHED_EVENT_TYPE_SLUGS.includes(slug);
}

/** The seven launch types the renderer + validator support today. */
export const LAUNCH_FIELD_TYPES: ReadonlySet<EventFieldType> =
  new Set<EventFieldType>([
    "text",
    "long_text",
    "numeric",
    "date",
    "time",
    "yes_no",
    "dropdown",
  ]);

export function isLaunchField(field: EventField): boolean {
  return LAUNCH_FIELD_TYPES.has(field.type);
}

/**
 * Defensively parse the untyped `fieldSchema` blob into our grammar. Returns an
 * empty field list rather than throwing if the shape is unexpected — a
 * malformed schema should render "no fields" gracefully, not crash the form.
 */
export function parseFieldSchema(raw: unknown): EventFieldSchema {
  if (
    raw &&
    typeof raw === "object" &&
    Array.isArray((raw as { fields?: unknown }).fields)
  ) {
    return raw as EventFieldSchema;
  }
  return { fields: [] };
}

/**
 * Build a zod schema dynamically from an event type's launch fields.
 *
 * This MUST mirror the backend `EventRecordSchemaValidator` so the client and
 * server agree on what "present" means — otherwise a user passes client
 * validation and then hits a server 400:
 *   - numeric: `0` is a valid answer; only a non-finite/non-number is missing.
 *   - yes_no:  `false` is a valid answer; the field is missing only if no
 *              boolean was set at all.
 *   - text / long_text / date / time: whitespace-only counts as missing.
 *   - dropdown: a *present* value must be one of `options`, even when optional
 *              (the record is immutable, so a bad value can never be fixed).
 *
 * Reserved/non-launch field types are skipped (not added to the schema), so a
 * future event type carrying them won't block submission of the launch fields.
 */
export function buildEventRecordZodSchema(
  schema: EventFieldSchema,
): z.ZodType<Record<string, unknown>> {
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const field of schema.fields) {
    if (!isLaunchField(field)) continue;
    shape[field.key] = zodForField(field);
  }

  return z.object(shape);
}

/**
 * A friendly "missing value" message phrased from the field's own label, e.g.
 * "Please enter a date of drill" / "Please enter an assembly point used".
 * Replaces Zod's raw type errors ("Invalid input: expected string, received
 * undefined"). Dropdowns are chosen, not typed, so they read "Please select…".
 */
export function requiredFieldMessage(field: EventField): string {
  const verb = field.type === "dropdown" ? "select" : "enter";
  const label = field.label.trim();
  // Lowercase only the first character so any embedded proper nouns survive.
  const phrase = label.charAt(0).toLowerCase() + label.slice(1);
  const article = /^[aeiou]/i.test(phrase) ? "an" : "a";
  return `Please ${verb} ${article} ${phrase}`;
}

function zodForField(field: EventField): z.ZodTypeAny {
  const { required, options } = field;
  const missing = requiredFieldMessage(field);

  switch (field.type) {
    case "numeric": {
      // `0` is valid. RHF gives us `undefined` for an untouched numeric input.
      const base = z.number({ error: missing }).finite();
      return required ? base : base.optional();
    }

    case "yes_no": {
      // `false` is a valid answer; required means a boolean must be set.
      return required ? z.boolean({ error: missing }) : z.boolean().optional();
    }

    case "dropdown": {
      const allowed = options ?? [];
      const inOptions = z
        .string()
        .refine((v) => allowed.includes(v), "Select a valid option");
      if (required) {
        // The `error` covers an unset (undefined) value; `min` covers an empty
        // string — both should read the same friendly message.
        return z
          .string({ error: missing })
          .trim()
          .min(1, missing)
          .pipe(inOptions);
      }
      // Optional: empty is fine, but any present value must be a valid option.
      return z.union([z.literal(""), z.undefined(), inOptions]).optional();
    }

    // text / long_text / date / time: string fields, whitespace-only = missing.
    default: {
      if (required) {
        return z.string({ error: missing }).trim().min(1, missing);
      }
      return z.string().optional();
    }
  }
}
